import { useAuth } from '@/hooks/useAuth'
import { getInitials, getShortName } from '@/utils/userRoles'
import React, { useEffect, useRef, useState } from 'react'
import { AiOutlineLogout } from 'react-icons/ai'
import { HiOutlineTemplate } from 'react-icons/hi'
import { MdExpandMore, MdVpnKey } from 'react-icons/md'

import { UserMenuContainer } from './styles'

interface IProps {
  onOpenCommitteeTemplates: () => void
  onUpdatePassword: () => void
  onLogout: () => void
}

/**
 * Área de conta do cabeçalho: quem está logado fica legível sem abrir nada — avatar com as
 * iniciais e primeiro nome — e o menu abre com os mesmos três destinos de sempre.
 */
export const UserMenu: React.FC<IProps> = ({
  onOpenCommitteeTemplates,
  onUpdatePassword,
  onLogout
}) => {
  const { user } = useAuth()

  const [open, setOpen] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const name = user?.name || ''
  const initials = getInitials(name)
  const shortName = getShortName(name)

  function close(focusTrigger = false) {
    setOpen(false)

    if (focusTrigger) {
      triggerRef.current?.focus()
    }
  }

  useEffect(() => {
    if (!open) {
      return
    }

    function handleClickOutside(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        close()
      }
    }

    const handleScroll = () => close()

    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScroll, true)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [open])

  useEffect(() => {
    if (open) {
      menuRef.current?.querySelector<HTMLButtonElement>('button')?.focus()
    }
  }, [open])

  function focusItem(direction: 1 | -1, from?: HTMLElement) {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('button') || []
    )

    if (!items.length) {
      return
    }

    const currentIndex = from ? items.indexOf(from as HTMLButtonElement) : -1

    const nextIndex =
      currentIndex === -1
        ? direction === 1
          ? 0
          : items.length - 1
        : (currentIndex + direction + items.length) % items.length

    items[nextIndex]?.focus()
  }

  function handleMenuKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault()
      close(true)
      return
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return
    }

    event.preventDefault()

    const target = event.target as HTMLElement

    focusItem(
      event.key === 'ArrowDown' ? 1 : -1,
      target.tagName === 'BUTTON' ? target : undefined
    )
  }

  /** Fecha antes de sair da tela: o menu não deve sobreviver à navegação. */
  function runAndClose(action: () => void) {
    close()
    action()
  }

  return (
    <UserMenuContainer ref={containerRef}>
      <div className="user_menu__divider" />

      <button
        type="button"
        ref={triggerRef}
        className={`user_menu__trigger ${open ? 'is-open' : ''}`}
        aria-label={`Conta de ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="user_menu__avatar">{initials}</span>
        <span className="user_menu__identity">
          <strong>{shortName}</strong>
        </span>
        <MdExpandMore className="user_menu__chevron" />
      </button>

      {open ? (
        <div
          className="user_menu__card"
          ref={menuRef}
          role="menu"
          aria-label={`Conta de ${name}`}
          onKeyDown={handleMenuKeyDown}
        >
          <div className="user_menu__header">
            <span className="user_menu__avatar is-card">{initials}</span>
            <div className="user_menu__header_text">
              <strong>{name}</strong>
              <small>{user?.email}</small>
            </div>
          </div>

          <div className="user_menu__group">
            <button
              type="button"
              role="menuitem"
              onClick={() => runAndClose(onOpenCommitteeTemplates)}
            >
              <HiOutlineTemplate size="1rem" /> Template de comitê
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => runAndClose(onUpdatePassword)}
            >
              <MdVpnKey size="1rem" /> Trocar senha
            </button>
          </div>

          <div className="user_menu__group is-footer">
            <button
              type="button"
              role="menuitem"
              className="is-danger"
              onClick={() => runAndClose(onLogout)}
            >
              <AiOutlineLogout size="1rem" /> Sair
            </button>
          </div>
        </div>
      ) : null}
    </UserMenuContainer>
  )
}
